import { Loader, VisuallyHidden } from '@mantine/core';
import { useReducedMotion } from '@mantine/hooks';
import { IconArrowDown } from '@tabler/icons-react';

import { PULL_REFRESH_HOLD, PULL_THRESHOLD, isArmed } from '@/components/pwa/pullGesture';
import { usePullToRefresh } from '@/components/pwa/usePullToRefresh';

const SIZE = 40;
const HIDDEN_OFFSET = SIZE + 8;
const SETTLE = 'transform 220ms cubic-bezier(0.2, 0.8, 0.2, 1), opacity 180ms ease';

export interface PullToRefreshProps {
  enabled: boolean;
}

export function PullToRefresh({ enabled }: PullToRefreshProps) {
  const state = usePullToRefresh(enabled);
  const reducedMotion = useReducedMotion() ?? false;
  const refreshing = state.phase === 'refreshing';
  const armed = isArmed(state);
  const progress = refreshing ? 1 : Math.min(state.pull / PULL_THRESHOLD, 1);
  const fingerDriven = state.phase === 'pulling' && !reducedMotion;
  const offset = reducedMotion ? PULL_REFRESH_HOLD : state.pull;
  const scale = reducedMotion ? 1 : 0.6 + 0.4 * progress;

  return (
    <>
      <div
        aria-hidden
        data-testid="pull-to-refresh"
        data-phase={state.phase}
        data-armed={armed || undefined}
        style={{
          position: 'fixed',
          top: 'calc(56px + env(safe-area-inset-top, 0px))',
          left: '50%',
          width: SIZE,
          height: SIZE,
          marginLeft: -SIZE / 2,
          zIndex: 99,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '50%',
          background: 'var(--surface-raised)',
          border: '1px solid var(--border-subtle)',
          boxShadow: 'var(--shadow-md)',
          color: 'var(--accent)',
          pointerEvents: 'none',
          opacity: progress,
          transform: `translate3d(0, ${offset - HIDDEN_OFFSET}px, 0) scale(${scale})`,
          transition: fingerDriven ? 'none' : reducedMotion ? 'opacity 150ms ease' : SETTLE,
          willChange: 'transform, opacity',
        }}
      >
        {refreshing ? (
          <Loader size={18} color="var(--accent)" />
        ) : (
          <IconArrowDown
            size={20}
            stroke={2.2}
            style={{
              transform: `rotate(${armed ? 180 : 0}deg)`,
              transition: reducedMotion ? 'none' : 'transform 160ms ease',
            }}
          />
        )}
      </div>
      <VisuallyHidden role="status" aria-live="polite">
        {refreshing ? 'Refreshing' : ''}
      </VisuallyHidden>
    </>
  );
}
